import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { uploadFile } from "@/components/ImageUpload";

// ─── Types ───────────────────────────────────────────────────────────────────
interface PostingWindow { start: string; end: string }
interface FacebookConfig {
  automationEnabled: boolean; pageId: string | null; graphVersion: string; claudeModel: string;
  postsPerDay: number; postingWindows: PostingWindow[]; shopGeneralMix: number; openBufferMins: number;
  minImageReuseDays: number; dedupLookback: number; claudeTimeoutSecs: number; claudeMaxRetries: number; publishMaxRetries: number;
}
interface QueuePost {
  id: number; status: string; postType: string; scheduledFor: string; caption: string | null;
  captionSource: string; adminLocked: boolean; storeId: number | null; storeName: string | null;
  menuItemId: number | null; itemName: string | null; mediaId: number | null;
  media: { id: number; objectPath: string; mediaType: string } | null;
  fbPostId: string | null; fbPostUrl: string | null; publishedAt: string | null;
  relocatedNote: string | null; lastGenerationError: string | null; lastPublishError: string | null;
}
interface MediaRow {
  id: number; storeId: number | null; storeName: string | null; mediaType: string; objectPath: string;
  notes: string | null; status: string; usageCount: number; lastPostedAt: string | null; createdAt: string;
  linkedItems: Array<{ id: number; name: string | null }>;
}
interface StoreRow { id: number; name: string; town: string }
interface ItemRow { id: number; name: string; available: boolean }

const fmt = (iso: string) => new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const badgeColor = (s: string) => ({
  scheduled: "bg-slate-200 text-slate-700", generating: "bg-amber-100 text-amber-800", ready: "bg-blue-100 text-blue-800",
  publishing: "bg-indigo-100 text-indigo-800", published: "bg-emerald-100 text-emerald-800", failed: "bg-red-100 text-red-800",
  skipped: "bg-gray-100 text-gray-500", rescheduled: "bg-purple-100 text-purple-700",
}[s] ?? "bg-slate-100 text-slate-600");

export default function AdminFacebook() {
  useAuth();
  const { toast } = useToast();
  const token = localStorage.getItem("ayrshire_token");
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState<"queue" | "media" | "settings" | "history">("queue");
  const [queue, setQueue] = useState<QueuePost[]>([]);
  const [history, setHistory] = useState<QueuePost[]>([]);
  const [media, setMedia] = useState<MediaRow[]>([]);
  const [stores, setStores] = useState<StoreRow[]>([]);
  const [cfg, setCfg] = useState<FacebookConfig | null>(null);
  const [tokenConfigured, setTokenConfigured] = useState(false);

  const loadQueue = useCallback(async () => {
    const r = await fetch("/api/admin/facebook/queue?limit=10", { headers: authHeaders });
    if (r.ok) setQueue((await r.json()).posts ?? []);
  }, []);
  const loadHistory = useCallback(async () => {
    const r = await fetch("/api/admin/facebook/history?limit=40", { headers: authHeaders });
    if (r.ok) setHistory((await r.json()).posts ?? []);
  }, []);
  const loadMedia = useCallback(async () => {
    const r = await fetch("/api/admin/facebook/media", { headers: authHeaders });
    if (r.ok) setMedia((await r.json()).media ?? []);
  }, []);
  const loadStores = useCallback(async () => {
    const r = await fetch("/api/admin/facebook/stores", { headers: authHeaders });
    if (r.ok) setStores((await r.json()).stores ?? []);
  }, []);
  const loadSettings = useCallback(async () => {
    const r = await fetch("/api/admin/facebook/settings", { headers: authHeaders });
    if (r.ok) { const d = await r.json(); setCfg(d.config); setTokenConfigured(d.tokenConfigured); }
  }, []);

  useEffect(() => { void loadSettings(); void loadStores(); void loadQueue(); void loadMedia(); }, []);
  useEffect(() => { if (tab === "history") void loadHistory(); if (tab === "queue") void loadQueue(); if (tab === "media") void loadMedia(); }, [tab]);
  // Auto-refresh the queue so generated/regenerated captions appear without a manual reload.
  useEffect(() => {
    if (tab !== "queue") return;
    const t = setInterval(() => { void loadQueue(); }, 8000);
    return () => clearInterval(t);
  }, [tab, loadQueue]);

  return (
    <AdminLayout>
      <div className="p-4 max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mb-1">Facebook Marketing</h1>
        <p className="text-sm text-gray-500 mb-4">Automatic Page posts — shops post only when open & accepting orders.</p>
        <div className="flex gap-2 mb-5 border-b">
          {(["queue", "media", "settings", "history"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px ${tab === t ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t}</button>
          ))}
        </div>

        {tab === "queue" && <QueueTab posts={queue} stores={stores} media={media} authHeaders={authHeaders} toast={toast} reload={loadQueue} />}
        {tab === "media" && <MediaTab media={media} stores={stores} authHeaders={authHeaders} toast={toast} reload={loadMedia} />}
        {tab === "settings" && cfg && <SettingsTab cfg={cfg} tokenConfigured={tokenConfigured} authHeaders={authHeaders} toast={toast} reload={loadSettings} />}
        {tab === "history" && <HistoryTab posts={history} />}
      </div>
    </AdminLayout>
  );
}

// ─── Queue ─────────────────────────────────────────────────────────────────
function QueueTab({ posts, stores, media, authHeaders, toast, reload }: {
  posts: QueuePost[]; stores: StoreRow[]; media: MediaRow[]; authHeaders: Record<string, string>; toast: ReturnType<typeof useToast>["toast"]; reload: () => Promise<void>;
}) {
  const act = async (id: number, path: string, body?: unknown) => {
    const r = await fetch(`/api/admin/facebook/posts/${id}/${path}`, { method: "POST", headers: authHeaders, body: body ? JSON.stringify(body) : undefined });
    if (r.ok) { toast({ title: "Done" }); await reload(); } else toast({ title: "Failed", variant: "destructive" });
  };
  const patch = async (id: number, body: unknown) => {
    const r = await fetch(`/api/admin/facebook/posts/${id}`, { method: "PATCH", headers: authHeaders, body: JSON.stringify(body) });
    if (r.ok) { toast({ title: "Saved" }); await reload(); } else toast({ title: "Failed", variant: "destructive" });
  };
  if (!posts.length) return <p className="text-sm text-slate-500">No scheduled posts yet. The planner fills the queue automatically once media is added and posting is configured.</p>;
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">The next {posts.length} scheduled posts (times shown in Europe/London).</p>
      {posts.map((p) => <QueueCard key={p.id} p={p} stores={stores} media={media} act={act} patch={patch} />)}
    </div>
  );
}

function QueueCard({ p, stores, media, act, patch }: {
  p: QueuePost; stores: StoreRow[]; media: MediaRow[]; act: (id: number, path: string, body?: unknown) => Promise<void>; patch: (id: number, body: unknown) => Promise<void>;
}) {
  const [caption, setCaption] = useState(p.caption ?? "");
  const [when, setWhen] = useState(() => { const d = new Date(p.scheduledFor); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); });
  useEffect(() => { setCaption(p.caption ?? ""); }, [p.caption]);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${badgeColor(p.status)}`}>{p.status}</span>
        <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{p.postType}</span>
        <span className="text-sm font-medium text-slate-800">{fmt(p.scheduledFor)}</span>
        {p.storeName && <span className="text-sm text-slate-500">· {p.storeName}{p.itemName ? ` — ${p.itemName}` : ""}</span>}
        <span className={`text-[11px] px-2 py-0.5 rounded ${p.captionSource === "admin_edited" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-500"}`}>{p.captionSource === "admin_edited" ? "edited by admin" : "generated"}</span>
      </div>
      {p.relocatedNote && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">⚠ {p.relocatedNote}</div>}
      {(p.lastGenerationError || p.lastPublishError) && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-2">{p.lastPublishError || p.lastGenerationError}</div>}
      <div className="flex gap-3">
        {p.media && (p.media.mediaType === "video"
          ? <video src={p.media.objectPath} className="w-28 h-28 rounded-lg object-cover border" muted />
          : <img src={p.media.objectPath} alt="" className="w-28 h-28 rounded-lg object-cover border" />)}
        <div className="flex-1 min-w-0">
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} placeholder={p.status === "scheduled" ? "Generating…" : "Caption"} className="w-full text-sm border rounded-lg px-2 py-1.5" />
          <div className="flex flex-wrap gap-2 mt-2">
            <button onClick={() => patch(p.id, { caption })} className="text-xs bg-primary text-white px-3 py-1.5 rounded-lg">Save caption</button>
            <button onClick={() => act(p.id, "regenerate")} className="text-xs bg-slate-100 px-3 py-1.5 rounded-lg">Regenerate</button>
            <button onClick={() => act(p.id, "publish-now")} className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg">Publish now</button>
            <button onClick={() => act(p.id, "skip")} className="text-xs bg-red-50 text-red-600 px-3 py-1.5 rounded-lg">Skip</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 items-center">
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="text-xs border rounded px-2 py-1" />
            <button onClick={() => patch(p.id, { scheduledFor: new Date(when).toISOString() })} className="text-xs bg-slate-100 px-3 py-1 rounded-lg">Reschedule</button>
            <select value={p.storeId ?? ""} onChange={(e) => patch(p.id, { storeId: e.target.value ? Number(e.target.value) : null })} className="text-xs border rounded px-2 py-1">
              <option value="">General (no shop)</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={p.mediaId ?? ""} onChange={(e) => patch(p.id, { mediaId: e.target.value ? Number(e.target.value) : null })} className="text-xs border rounded px-2 py-1">
              <option value="">No media</option>
              {media.filter((m) => m.status === "approved").map((m) => <option key={m.id} value={m.id}>#{m.id} {m.mediaType}{m.storeName ? ` · ${m.storeName}` : ""}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Media ─────────────────────────────────────────────────────────────────
function MediaTab({ media, stores, authHeaders, toast, reload }: {
  media: MediaRow[]; stores: StoreRow[]; authHeaders: Record<string, string>; toast: ReturnType<typeof useToast>["toast"]; reload: () => Promise<void>;
}) {
  const [uploading, setUploading] = useState(false);
  const [storeId, setStoreId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<ItemRow[]>([]);
  const [linkIds, setLinkIds] = useState<number[]>([]);
  // Gallery filter (separate from the upload-association dropdown above).
  // "" = all, "__general__" = general/no-shop, otherwise a store id.
  const [filterStoreId, setFilterStoreId] = useState<string>("");
  const shownMedia = filterStoreId === ""
    ? media
    : filterStoreId === "__general__"
      ? media.filter((m) => m.storeId == null)
      : media.filter((m) => String(m.storeId) === filterStoreId);

  useEffect(() => {
    setLinkIds([]);
    if (!storeId) { setItems([]); return; }
    void fetch(`/api/admin/facebook/stores/${storeId}/items`, { headers: authHeaders }).then(async (r) => { if (r.ok) setItems((await r.json()).items ?? []); });
  }, [storeId]);

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: Array<{ objectPath: string; mediaType: string }> = [];
      for (const f of Array.from(files)) {
        const path = await uploadFile(f);
        uploaded.push({ objectPath: path, mediaType: f.type.startsWith("video/") ? "video" : "image" });
      }
      const r = await fetch("/api/admin/facebook/media", { method: "POST", headers: authHeaders, body: JSON.stringify({ items: uploaded.map((u) => ({ ...u, storeId: storeId ? Number(storeId) : null, notes: notes || undefined, menuItemIds: linkIds })) }) });
      if (r.ok) { toast({ title: `Uploaded ${uploaded.length}` }); setNotes(""); setLinkIds([]); await reload(); }
      else toast({ title: "Save failed", variant: "destructive" });
    } catch (e) { toast({ title: (e as Error).message, variant: "destructive" }); }
    finally { setUploading(false); }
  };

  const patchMedia = async (id: number, body: unknown) => {
    const r = await fetch(`/api/admin/facebook/media/${id}`, { method: "PATCH", headers: authHeaders, body: JSON.stringify(body) });
    if (r.ok) await reload(); else toast({ title: "Failed", variant: "destructive" });
  };

  return (
    <div>
      <div className="bg-slate-50 border rounded-xl p-4 mb-5">
        <h2 className="font-semibold text-sm mb-3">Upload media (images & video)</h2>
        <div className="flex flex-wrap gap-3 items-start">
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="text-sm border rounded-lg px-2 py-1.5">
            <option value="">General (no shop)</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Factual notes (optional)" className="text-sm border rounded-lg px-2 py-1.5 flex-1 min-w-[180px]" />
        </div>
        {items.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-slate-500 mb-1">Link to specific dishes (optional — stops the AI guessing the food):</p>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-auto">
              {items.map((it) => (
                <button key={it.id} onClick={() => setLinkIds((s) => s.includes(it.id) ? s.filter((x) => x !== it.id) : [...s, it.id])}
                  className={`text-xs px-2 py-1 rounded-full border ${linkIds.includes(it.id) ? "bg-primary text-white border-primary" : "bg-white border-slate-200"}`}>{it.name}</button>
              ))}
            </div>
          </div>
        )}
        <label className="inline-block mt-3">
          <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => void onFiles(e.target.files)} />
          <span className={`inline-block text-sm px-4 py-2 rounded-lg cursor-pointer ${uploading ? "bg-slate-300" : "bg-primary text-white"}`}>{uploading ? "Uploading…" : "Choose files"}</span>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="text-sm text-slate-600">Show:</label>
        <select value={filterStoreId} onChange={(e) => setFilterStoreId(e.target.value)} className="text-sm border rounded-lg px-2 py-1.5">
          <option value="">All shops</option>
          <option value="__general__">General (no shop)</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <span className="text-xs text-slate-400">{shownMedia.length} of {media.length}</span>
      </div>

      {shownMedia.length === 0 ? (
        <p className="text-sm text-slate-500 py-6">No media{filterStoreId ? " for this shop" : ""} yet.</p>
      ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {shownMedia.map((m) => (
          <div key={m.id} className="bg-white border rounded-xl overflow-hidden">
            {m.mediaType === "video" ? <video src={m.objectPath} className="w-full h-32 object-cover" muted /> : <img src={m.objectPath} alt="" className="w-full h-32 object-cover" />}
            <div className="p-2 text-xs space-y-1">
              <div className="text-slate-600 truncate">{m.storeName ?? "General"}</div>
              <div className="text-slate-400">used {m.usageCount}× {m.lastPostedAt ? `· last ${fmt(m.lastPostedAt)}` : ""}</div>
              {m.linkedItems.length > 0 && <div className="text-slate-400 truncate">🍴 {m.linkedItems.map((i) => i.name).join(", ")}</div>}
              <select value={m.status} onChange={(e) => patchMedia(m.id, { status: e.target.value })} className="w-full border rounded px-1 py-0.5">
                {["approved", "paused", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

// ─── Settings ────────────────────────────────────────────────────────────────
function SettingsTab({ cfg, tokenConfigured, authHeaders, toast, reload }: {
  cfg: FacebookConfig; tokenConfigured: boolean; authHeaders: Record<string, string>; toast: ReturnType<typeof useToast>["toast"]; reload: () => Promise<void>;
}) {
  const [f, setF] = useState<FacebookConfig>(cfg);
  const [tokenInput, setTokenInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [conn, setConn] = useState<string>("");

  const save = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        automationEnabled: f.automationEnabled, pageId: f.pageId ?? "", graphVersion: f.graphVersion, claudeModel: f.claudeModel,
        postsPerDay: f.postsPerDay, postingWindows: f.postingWindows, shopGeneralMix: f.shopGeneralMix, openBufferMins: f.openBufferMins,
        minImageReuseDays: f.minImageReuseDays, dedupLookback: f.dedupLookback, claudeTimeoutSecs: f.claudeTimeoutSecs,
        claudeMaxRetries: f.claudeMaxRetries, publishMaxRetries: f.publishMaxRetries,
      };
      if (tokenInput.trim()) body["pageAccessToken"] = tokenInput.trim();
      const r = await fetch("/api/admin/facebook/settings", { method: "PUT", headers: authHeaders, body: JSON.stringify(body) });
      if (r.ok) { toast({ title: "Settings saved" }); setTokenInput(""); await reload(); } else toast({ title: "Save failed", variant: "destructive" });
    } finally { setSaving(false); }
  };
  const testConn = async () => {
    setConn("Testing…");
    const r = await fetch("/api/admin/facebook/connection-test", { method: "POST", headers: authHeaders });
    const d = await r.json();
    setConn(d.ok ? `✓ Connected to "${d.pageName}"${d.grantedTasks ? ` (tasks: ${d.grantedTasks.join(", ")})` : ""}` : `✗ ${d.error}`);
  };
  const num = (k: keyof FacebookConfig, label: string, min = 0, max = 999, step = 1) => (
    <label className="block text-sm">{label}
      <input type="number" min={min} max={max} step={step} value={f[k] as number} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} className="border rounded px-2 py-1 w-24 ml-2" />
    </label>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <div className={`rounded-xl border p-4 ${f.automationEnabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50"}`}>
        <label className="flex items-center gap-3 font-medium text-sm">
          <input type="checkbox" checked={f.automationEnabled} onChange={(e) => setF({ ...f, automationEnabled: e.target.checked })} />
          Automatic publishing {f.automationEnabled ? "ON — posts go live at their scheduled time" : "OFF — nothing is published (queue still builds)"}
        </label>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <h2 className="font-semibold text-sm">Facebook Page</h2>
        <label className="block text-sm">Page ID
          <input value={f.pageId ?? ""} onChange={(e) => setF({ ...f, pageId: e.target.value })} className="border rounded px-2 py-1 ml-2 w-56 max-w-full" />
        </label>
        <label className="block text-sm">Page access token {tokenConfigured && <span className="text-emerald-600 text-xs">(saved)</span>}
          <input type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} placeholder={tokenConfigured ? "•••••• (leave blank to keep)" : "paste token"} className="border rounded px-2 py-1 ml-2 w-72 max-w-full" />
        </label>
        <label className="block text-sm">Graph version
          <input value={f.graphVersion} onChange={(e) => setF({ ...f, graphVersion: e.target.value })} className="border rounded px-2 py-1 ml-2 w-24" />
        </label>
        <button onClick={testConn} className="text-xs bg-slate-100 px-3 py-1.5 rounded-lg">Test connection</button>
        {conn && <div className="text-xs text-slate-600">{conn}</div>}
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <h2 className="font-semibold text-sm">Schedule & content</h2>
        {num("postsPerDay", "Posts per day", 0, 12)}
        <div>
          <p className="text-sm mb-1">Posting windows (Europe/London)</p>
          {f.postingWindows.map((w, i) => (
            <div key={i} className="flex items-center gap-2 mb-1">
              <input type="time" value={w.start} onChange={(e) => setF({ ...f, postingWindows: f.postingWindows.map((x, j) => j === i ? { ...x, start: e.target.value } : x) })} className="border rounded px-2 py-1" />
              <span>–</span>
              <input type="time" value={w.end} onChange={(e) => setF({ ...f, postingWindows: f.postingWindows.map((x, j) => j === i ? { ...x, end: e.target.value } : x) })} className="border rounded px-2 py-1" />
              <button onClick={() => setF({ ...f, postingWindows: f.postingWindows.filter((_, j) => j !== i) })} className="text-xs text-red-500">remove</button>
            </div>
          ))}
          <button onClick={() => setF({ ...f, postingWindows: [...f.postingWindows, { start: "12:00", end: "13:00" }] })} className="text-xs bg-slate-100 px-2 py-1 rounded">+ window</button>
        </div>
        <label className="block text-sm">Shop vs general mix: {Math.round(f.shopGeneralMix * 100)}% shop
          <input type="range" min={0} max={1} step={0.05} value={f.shopGeneralMix} onChange={(e) => setF({ ...f, shopGeneralMix: Number(e.target.value) })} className="w-full" />
        </label>
        {num("openBufferMins", "Closing buffer (mins)", 0, 240)}
        {num("minImageReuseDays", "Min image reuse (days)", 0, 90)}
        {num("dedupLookback", "Duplicate lookback (posts)", 5, 200)}
        <label className="block text-sm">Caption model
          <input value={f.claudeModel} onChange={(e) => setF({ ...f, claudeModel: e.target.value })} className="border rounded px-2 py-1 ml-2 w-72 max-w-full" />
        </label>
        {num("claudeTimeoutSecs", "Claude timeout (secs)", 20, 300)}
        {num("claudeMaxRetries", "Claude max retries", 1, 6)}
        {num("publishMaxRetries", "Publish max retries", 1, 10)}
      </div>

      <button onClick={save} disabled={saving} className="bg-primary text-white px-5 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? "Saving…" : "Save settings"}</button>
    </div>
  );
}

// ─── History ─────────────────────────────────────────────────────────────────
function HistoryTab({ posts }: { posts: QueuePost[] }) {
  if (!posts.length) return <p className="text-sm text-slate-500">No published posts yet.</p>;
  return (
    <div className="space-y-2">
      {posts.map((p) => (
        <div key={p.id} className="bg-white border rounded-lg p-3 flex items-center gap-3">
          {p.media && (p.media.mediaType === "video" ? <video src={p.media.objectPath} className="w-14 h-14 rounded object-cover" muted /> : <img src={p.media.objectPath} alt="" className="w-14 h-14 rounded object-cover" />)}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-[11px] px-2 py-0.5 rounded ${badgeColor(p.status)}`}>{p.status}</span>
              <span className="text-xs text-slate-500">{p.publishedAt ? fmt(p.publishedAt) : fmt(p.scheduledFor)}</span>
              {p.storeName && <span className="text-xs text-slate-500">· {p.storeName}</span>}
            </div>
            <p className="text-sm text-slate-700 truncate">{p.caption}</p>
            {p.lastPublishError && <p className="text-xs text-red-600">{p.lastPublishError}</p>}
          </div>
          {p.fbPostUrl && <a href={p.fbPostUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600">View →</a>}
        </div>
      ))}
    </div>
  );
}
